/* eslint-disable @typescript-eslint/no-unused-vars */
/** @file Payment processor/gateway section */

import React, { useState } from 'react';
import { Box, Divider, SxProps } from '@mui/material';
import { PaymentGatewayType, PaymentCredentialData } from '../../../store/modules/merchant/types';
import { testId as parentTestId } from '../util';
import { GatewayAccordion } from './PaymentGatewayAccordions';
import { useMerchant } from '../../../contexts/MerchantContext';

export const testId = `${parentTestId}-PAYMENT_GATEWAY`;

// Inputs /////////////////////////////////////////////////////////////////////

const styles: { [id: string]: SxProps } = {
  title: {
    color: 'text.primary',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  description: {
    color: 'text.secondary',
    fontSize: '12px',
  },
  accContainer: {
    border: `1px solid`,
    borderColor: 'border.main',
    borderRadius: '8px',
    marginTop: 2,
    width: { xs: '100%', sm: '448px' },
    '& > .MuiPaper-root': {
      borderRadius: '8px !important',
      boxShadow: 'none',
    },
  },
  accTitle: {
    color: 'text.primary',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '4px',
  },
  accTitleAndIconContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  accDescription: {
    color: 'text.secondary',
    fontSize: '10px',
    lineHeight: '16px',
  },
  accDescriptionCallout: {
    fontWeight: 'bold',
  },
  accSummary: {
    '& > div': {
      justifyContent: 'space-between',
    },
  },
  captureAcc: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
};

type Props = {
  credentials?: Partial<Record<PaymentGatewayType, PaymentCredentialData>>;
  onCredentialChange: (gateway: PaymentGatewayType, data: PaymentCredentialData) => void;
  onSelectedGatewayChange: (selectedGateway: PaymentGatewayType | undefined) => void;
  selectedGateway?: PaymentGatewayType;
  isPaymentPlatformEnabled?: boolean;
  // cancelChanges?: boolean;
  sx?: SxProps;
};

/**
 * Payment Gateway section of Payment and Processing
 * @param {Props} props Component Properties
 * @returns {JSX.Element}
 */
const PaymentGateway = ({
  credentials = {},
  selectedGateway,
  onSelectedGatewayChange,
  onCredentialChange,
  isPaymentPlatformEnabled = false,
  sx = {},
}: Props) => {
  const { canUpdateMerchantSettings } = useMerchant();
  const [expandedAccordions, setExpandedAccordions] = useState<Set<PaymentGatewayType>>(
    new Set(selectedGateway ? [selectedGateway] : []),
  );

  const hasModifyAccess = canUpdateMerchantSettings();
  const gateways: PaymentGatewayType[] = [
    'Fiserv',
    'WPG',
    'Adyen',
    'Braintree',
    'Express',
    'RAFT',
    'Stripe',
    'VAP',
    'Payrix',
    'Maverick',
  ];

  return (
    <Box sx={sx} component="section" data-testid={testId}>
      <Box sx={styles.title}>Payment Gateway</Box>
      <Box sx={styles.description}>
        Use this section to select your preferred payment gateway and to input your credentials.
      </Box>
      <Box sx={styles.accContainer}>
        {gateways.map((gateway, index) => {
          return (
            <React.Fragment key={gateway}>
              <GatewayAccordion
                gateway={gateway}
                onSelectToggle={() => {
                  const newValue = selectedGateway === gateway ? undefined : gateway;
                  if (newValue) {
                    setExpandedAccordions(new Set([gateway]));
                  }
                  onSelectedGatewayChange(selectedGateway === gateway ? undefined : gateway);
                }}
                isExpanded={expandedAccordions.has(gateway)}
                onExpandToggle={() => {
                  const newExpandedAccordions = new Set(expandedAccordions);
                  if (expandedAccordions.has(gateway)) {
                    newExpandedAccordions.delete(gateway);
                  } else {
                    newExpandedAccordions.add(gateway);
                  }
                  setExpandedAccordions(newExpandedAccordions);
                }}
                isSelected={selectedGateway === gateway}
                disabled={!hasModifyAccess}
                onChange={(newValue) => onCredentialChange(gateway, newValue)}
                value={credentials[gateway]}
                isPaymentPlatformEnabled={isPaymentPlatformEnabled}
              />
              {index < gateways.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default PaymentGateway;
